import { TypeProgram_metaWithoutUnresolvableLinksResponse } from "@2u-landing-pages/types";
import { PricingAPIPayload } from "./fetch-program-pricing";
import { replaceDisplayAmount } from "./formatting";

type ProgramMetaFields = TypeProgram_metaWithoutUnresolvableLinksResponse["fields"];

/**
 * Codegen narrows the dropdown to its Contentful options, so editing those options
 * breaks the build here rather than silently falling back to full price.
 */
export type InstallmentCalculation = NonNullable<ProgramMetaFields["installment_price_calculation"]>;

/** Values stored by the `installment_price_calculation` dropdown. */
export const INSTALLMENT_CALCULATION = {
  fullPrice: "Full Price (API Default)",
  applyTuitionDiscount: "Apply Tuition Discount",
} as const satisfies Record<string, InstallmentCalculation>;

/**
 * Deliberately wider than the generated field types: Contentful's option validation
 * is not retroactive, so an entry can still hold a calculation outside the union.
 */
type InstallmentSettingsFields = {
  enable_installments?: boolean;
  installment_price_calculation?: string;
};

export type InstallmentSettings = {
  enabled: boolean;
  calculation: InstallmentCalculation;
};

/**
 * Reads the two installment fields off a Program Meta entry. Both default to the
 * inert option, so an entry predating the migration behaves as switched off.
 */
export const readInstallmentSettings = (fields?: InstallmentSettingsFields): InstallmentSettings => ({
  enabled: fields?.enable_installments === true,
  calculation:
    fields?.installment_price_calculation === INSTALLMENT_CALCULATION.applyTuitionDiscount
      ? INSTALLMENT_CALCULATION.applyTuitionDiscount
      : INSTALLMENT_CALCULATION.fullPrice,
});

export type InstallmentPayment = {
  label: string;
  /** Pre-formatted by the API, e.g. "Oct 13, 2026". */
  dueDate: string;
  /** Carries the API's own currency symbol and suffix. */
  amountDisplay: string;
  amount: number;
};

export type InstallmentPlan = {
  paymentCount: number;
  title: string;
  description: string;
  payments: InstallmentPayment[];
};

const toCents = (amount: number): number => Math.round(amount * 100);

/**
 * Half-up rounding of `cents` reduced by `discount` percent, kept in integers
 * throughout. Scaling by a fractional multiplier instead loses a cent at exact
 * half-cent boundaries, because a value such as 1.005 is held as 1.00499…, so
 * $2.01 at 50% off would advertise $1.00 rather than $1.01.
 */
const discountCents = (cents: number, discount: number): number => {
  const scaled = cents * (100 - discount);
  const remainder = scaled % 100;

  return (scaled - remainder) / 100 + (remainder >= 50 ? 1 : 0);
};

/**
 * Applies the tuition discount to each payment, absorbing the rounding residual
 * into the last one so the schedule still sums to the discounted total. Rounding
 * each payment independently would leave the schedule a cent or two off the price
 * shown on screen.
 */
const discountAmounts = (amounts: number[], discount: number): number[] => {
  const cents = amounts.map(toCents);
  const discountedTotal = discountCents(
    cents.reduce((sum, amount) => sum + amount, 0),
    discount,
  );

  const leading = cents.slice(0, -1).map((amount) => discountCents(amount, discount));
  const trailing = discountedTotal - leading.reduce((sum, amount) => sum + amount, 0);

  return [...leading, trailing].map((amount) => amount / 100);
};

type InstallmentPricingFields = Pick<PricingAPIPayload, "installments">;

/**
 * Reads the payment schedule the Pricing API supplies. Nothing about the split
 * is computed here: the amounts, dates, and payment count are all 2U's. Returns
 * null whenever no schedule should render, so callers can hide their copy
 * instead of showing a half-built sentence.
 */
export const resolveInstallmentPlan = ({
  pricing,
  settings,
  tuitionDiscount,
}: {
  pricing?: InstallmentPricingFields;
  settings: InstallmentSettings;
  tuitionDiscount?: number;
}): InstallmentPlan | null => {
  if (!settings.enabled) return null;

  const installments = pricing?.installments;

  // The disabled branch of the union carries no schedule to read.
  if (!installments?.enabled) return null;

  const { schedule, numberOfInstallments, title, description } = installments;
  if (!Array.isArray(schedule) || schedule.length === 0) return null;

  const amounts = schedule.map((payment) => parseFloat(payment.amountDue));

  const applyDiscount =
    settings.calculation === INSTALLMENT_CALCULATION.applyTuitionDiscount &&
    Number.isFinite(tuitionDiscount) &&
    (tuitionDiscount as number) > 0;

  // Showing an undiscounted amount where a discount was configured would
  // misprice the programme, so an unreadable amount yields no schedule at all.
  if (applyDiscount && amounts.some((amount) => !Number.isFinite(amount))) return null;

  const resolvedAmounts = applyDiscount ? discountAmounts(amounts, tuitionDiscount as number) : amounts;

  return {
    paymentCount: numberOfInstallments ?? schedule.length,
    title: title ?? "",
    description: description ?? "",
    payments: schedule.map((payment, index) => ({
      label: payment.paymentLabel,
      dueDate: payment.dueDate,
      amount: resolvedAmounts[index],
      amountDisplay: applyDiscount
        ? replaceDisplayAmount(payment.amountDueDisplay, resolvedAmounts[index].toFixed(2))
        : payment.amountDueDisplay,
    })),
  };
};

/**
 * Deliberately matches the bare `installments` key as well as the token
 * families, so a nested attempt such as `installments.schedule` resolves blank
 * rather than stringifying the payload object.
 */
export const INSTALLMENT_TOKEN_PREFIX = "installments";

const AMOUNT_DUE_TOKEN = /^installments_amount_due_(\d+)$/;
const DUE_DATE_TOKEN = /^installments_due_date_(\d+)$/;

/** Payments are addressed from 1 in content, e.g. `installments_amount_due_1`. */
const paymentAt = (plan: InstallmentPlan, index: string): InstallmentPayment | undefined =>
  plan.payments[Number(index) - 1];

/**
 * Resolves an `{{pricing_api.installments_*}}` token. Anything unavailable — no
 * plan, an index past the end of the schedule, an unrecognised token — yields an
 * empty string so the surrounding copy collapses rather than breaking.
 */
export const resolveInstallmentToken = (token: string, plan: InstallmentPlan | null): string | number => {
  if (!plan) return "";

  if (token === "installments_count") return plan.paymentCount;

  const amountDue = token.match(AMOUNT_DUE_TOKEN);
  if (amountDue) return paymentAt(plan, amountDue[1])?.amountDisplay ?? "";

  const dueDate = token.match(DUE_DATE_TOKEN);
  if (dueDate) return paymentAt(plan, dueDate[1])?.dueDate ?? "";

  return "";
};
